// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, generatePath } from 'react-router-dom';
import {
    Box,
    Button,
    Columns,
    Divider,
    Heading,
    Stack,
    Text,
    Icon,
    useToast
} from '@locus-taxy/neo-ui';

import { ApiStatusEnum } from 'common/enums/API';
import useClientId from 'common/hooks/useClientId';

import Skeleton from 'platform/settings/modules/orchestration/components/Skeleton';
import useGetRulesCount from './useGetRulesCount';

type Props = {
    translationPath: $FixMe
};

const RuleConfiguration = ({ translationPath }: Props): React.Node => {
    const { t } = useTranslation('platform');

    const history = useHistory();
    const clientId = useClientId();
    const { showToast } = useToast();

    const rulesCountQuery = useGetRulesCount({
        onError: () => {
            showToast({
                title: t(`${translationPath}.errorMessage`),
                palette: 'danger',
                action: t('translations:retry'),
                icon: (
                    <Icon icon="cross_circle" size="xLarge" color="red.600" />
                ),
                onAction: () => {
                    rulesCountQuery.refetch();
                }
            });
        }
    });

    const handleViewExecutionRules = () => {
        history.push({
            pathname: generatePath(
                '/client/:clientId/settings/shipflex/rules',
                {
                    clientId
                }
            )
        });
    };

    const renderRulesCount = () => {
        if (rulesCountQuery.status === ApiStatusEnum.Loading) {
            return (
                <Box width="50px">
                    <Skeleton height={20} borderRadius={3} count={1} />
                </Box>
            );
        }

        if (rulesCountQuery.status === ApiStatusEnum.Error) {
            return null;
        }

        return (
            <>
                <Box
                    height="4px"
                    width="4px"
                    backgroundColor="grey.600"
                    borderRadius="50%"
                />
                <Text size="medium" color="grey.600">
                    {`${rulesCountQuery.data} ${t('rule', {
                        count: rulesCountQuery.data
                    })}`}
                </Text>
            </>
        );
    };

    return (
        <Stack space="medium">
            <Heading size="large" color="grey.900">
                {t(`${translationPath}.label`)}
            </Heading>

            <Box backgroundColor="white" padding={4} borderRadius="4px">
                <Stack space="xSmall">
                    <Columns space="small" alignY="center">
                        <Text size="medium" color="grey.900">
                            {t(`${translationPath}.executionRules`)}
                        </Text>
                        {renderRulesCount()}
                    </Columns>

                    <Divider color="grey.200" />

                    <Text size="medium" color="grey.600">
                        {t(`${translationPath}.message`)}
                    </Text>

                    <Box ml="auto">
                        <Button
                            palette="primary"
                            onClick={handleViewExecutionRules}
                        >
                            {t(`${translationPath}.viewExecutionRules`)}
                        </Button>
                    </Box>
                </Stack>
            </Box>
        </Stack>
    );
};

export default RuleConfiguration;
